import { useState, useEffect, useCallback } from 'react';
import { ExerciseService, type ExerciseSearchParams, type ExerciseSearchResult } from '../services/exerciseService';
import type { Exercise } from '../services/types';

export interface UseExerciseSearchOptions {
  initialRowsPerPage?: number;
  enabled?: boolean;
}

export interface UseExerciseSearchResult {
  // Data
  exercises: Exercise[];
  loading: boolean;
  totalCount: number;

  // Filter state
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedMuscleGroup: string;
  setSelectedMuscleGroup: (group: string) => void;
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  selectedEquipment: string;
  setSelectedEquipment: (equipment: string) => void;
  selectedDifficulty: string;
  setSelectedDifficulty: (difficulty: string) => void;

  // Filter options
  muscleGroups: string[];
  categories: string[];
  equipment: string[];
  difficulties: string[];

  // Pagination
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (rowsPerPage: number) => void;
  handleChangePage: (event: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;

  // Actions
  clearFilters: () => void;
  refresh: () => void;
  reloadFilterOptions: () => void;
}

const DIFFICULTIES = ['beginner', 'intermediate', 'advanced'];

export function useExerciseSearch(options: UseExerciseSearchOptions = {}): UseExerciseSearchResult {
  const { initialRowsPerPage = 25, enabled = true } = options;

  // Data state
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMuscleGroup, setSelectedMuscleGroup] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  // Filter options
  const [muscleGroups, setMuscleGroups] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [equipment, setEquipment] = useState<string[]>([]);

  const loadFilterOptions = useCallback(async () => {
    try {
      const [muscleGroupsData, categoriesData, equipmentData] = await Promise.all([
        ExerciseService.getMuscleGroups(),
        ExerciseService.getCategories(),
        ExerciseService.getEquipmentTypes(),
      ]);
      setMuscleGroups(muscleGroupsData);
      setCategories(categoriesData);
      setEquipment(equipmentData);
    } catch (err) {
      console.error('Failed to load filter options:', err);
    }
  }, []);

  const doSearch = useCallback(async () => {
    if (!enabled) return;
    try {
      setLoading(true);
      const params: ExerciseSearchParams = {
        q: searchQuery,
        muscleGroup: selectedMuscleGroup,
        category: selectedCategory,
        equipment: selectedEquipment,
        difficulty: selectedDifficulty,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        parentOnly: true,
      };

      const result: ExerciseSearchResult = await ExerciseService.searchExercises(params);
      setExercises(result.exercises);
      setTotalCount(result.totalCount);
    } catch (err) {
      console.error('Failed to load exercises:', err);
    } finally {
      setLoading(false);
    }
  }, [enabled, searchQuery, selectedMuscleGroup, selectedCategory, selectedEquipment, selectedDifficulty, page, rowsPerPage]);

  // Initial load of filter options
  useEffect(() => {
    if (enabled) {
      loadFilterOptions();
    }
  }, [enabled, loadFilterOptions]);

  // Search when filters/pagination change
  useEffect(() => {
    doSearch();
  }, [doSearch]);

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedMuscleGroup('');
    setSelectedCategory('');
    setSelectedEquipment('');
    setSelectedDifficulty('');
    setPage(0);
  }, []);

  const handleChangePage = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  return {
    exercises,
    loading,
    totalCount,
    searchQuery,
    setSearchQuery,
    selectedMuscleGroup,
    setSelectedMuscleGroup,
    selectedCategory,
    setSelectedCategory,
    selectedEquipment,
    setSelectedEquipment,
    selectedDifficulty,
    setSelectedDifficulty,
    muscleGroups,
    categories,
    equipment,
    difficulties: DIFFICULTIES,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    clearFilters,
    refresh: doSearch,
    reloadFilterOptions: loadFilterOptions,
  };
}
